/**
 * Date / time formatting helpers.
 *
 * Centralised so every screen renders the same way — previously each
 * page called `new Date(iso).toLocaleString()` with subtly different
 * options, producing inconsistent UI (e.g. "5/8/2026" in one place
 * and "May 8, 2026" in another).
 *
 * Conventions:
 *   formatDateTime — short locale string with date + time (default for
 *                    audit timestamps, appointment times, etc.).
 *   formatDate     — date only (e.g. "May 8, 2026").
 *   formatTime     — time only (e.g. "3:30 PM").
 *   formatRange    — "<start> → <end>" composed from the two above.
 *   toLocalInput   — value for `<input type="datetime-local">`.
 *
 * All helpers tolerate `null | undefined` and return `'—'` so call
 * sites don't have to ternary-guard each render.
 */
import { readerLocale } from '../i18n/language';

const DASH = '—';

/** Anything the date helpers accept: an ISO string, a Date, or empty. */
type DateInput = string | Date | null | undefined;

function asDate(value: DateInput): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDateTime(value: DateInput): string {
  const d = asDate(value);
  if (!d) return DASH;
  return d.toLocaleString(readerLocale());
}

export function formatDate(value: DateInput): string {
  const d = asDate(value);
  if (!d) return DASH;
  return d.toLocaleDateString(readerLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatTime(value: DateInput): string {
  const d = asDate(value);
  if (!d) return DASH;
  return d.toLocaleTimeString(readerLocale(), { hour: 'numeric', minute: '2-digit' });
}

export function formatRange(
  start: DateInput,
  end: DateInput,
): string {
  return `${formatDateTime(start)} → ${formatDateTime(end)}`;
}

/** Convert a Date (or ISO string) to the value `<input type="datetime-local">` expects. */
export function toLocalInput(value: string | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Inverse of `toLocalInput` — datetime-local has no TZ so the result
 *  is interpreted as the user's local wall-clock time. */
export function fromLocalInput(s: string): Date {
  return new Date(s);
}

/** Format a money amount that came back from the server as a string
 *  (Pydantic Numeric → JSON string for precision). Falls back to the
 *  raw value when the currency is non-standard.
 *
 *  Both `amount` and `currency` tolerate `null | undefined` and render
 *  `'—'` — a product whose terms funds could not answer carries null
 *  min_investment / currency_code, and this must never guess a currency
 *  (e.g. defaulting to MXN) for an amount that has none. */
export function formatMoney(
  amount: string | number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount === null || amount === undefined || !currency) return DASH;
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (Number.isNaN(n)) return String(amount);
  try {
    return new Intl.NumberFormat(readerLocale(), {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}
